import { useState } from 'react'
import type { CatalogBulkPatch } from '~/api/catalog'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import { Select } from '~/components/ui/select'

// 카탈로그 "일괄 편집" 다이얼로그. 한 번에 한 필드만 바꾼다(분류/규모/단계/데이터유형/결정형).
// 필드를 고르면 그 필드의 값 입력 UI를 보여주고, 적용은 위험 작업이라 ConfirmDialog로 한 번 더 확인받는다.
// 실제 호출/토스트/새로고침은 상위(CatalogPage)가 onApply로 처리한다.

type FieldKey =
    | 'classification'
    | 'scale'
    | 'assessmentType'
    | 'dataType'
    | 'isDeterministic'

// 편집 가능한 필드 목록(라벨 + 선택지). 선택지의 첫 값을 기본값으로 둔다.
const FIELDS: {
    key: FieldKey
    label: string
    options: { value: string; label: string }[]
}[] = [
    {
        key: 'classification',
        label: '분류',
        options: [
            { value: '선행요건', label: '선행요건' },
            { value: 'HACCP관리', label: 'HACCP관리' },
        ],
    },
    {
        key: 'scale',
        label: '규모',
        options: [
            { value: '일반', label: '일반' },
            { value: '소규모', label: '소규모' },
        ],
    },
    {
        key: 'assessmentType',
        label: '단계',
        options: [
            { value: '', label: '(없음)' },
            { value: '인증', label: '인증' },
            { value: '정기', label: '정기' },
        ],
    },
    {
        key: 'dataType',
        label: '데이터유형',
        options: [
            { value: '', label: '(없음)' },
            { value: '정량', label: '정량' },
            { value: '정성', label: '정성' },
        ],
    },
    {
        key: 'isDeterministic',
        label: '결정형',
        options: [
            { value: 'true', label: '결정형(예)' },
            { value: 'false', label: '비결정형(아니오)' },
        ],
    },
]

// 선택한 필드/값 문자열 → API patch(한 필드). isDeterministic만 boolean으로 변환한다.
function toPatch(field: FieldKey, value: string): CatalogBulkPatch {
    if (field === 'isDeterministic')
        return { isDeterministic: value === 'true' }
    return { [field]: value } as CatalogBulkPatch
}

export function BulkEditDialog({
    count,
    busy,
    onApply,
    onClose,
}: {
    /** 적용 대상 항목 수(확인 문구에 사용). */
    count: number
    /** 적용 진행 중 — 버튼 비활성화. */
    busy: boolean
    /** 위험 확인까지 통과한 patch를 상위로 넘긴다(실제 호출/토스트/새로고침은 상위 책임). */
    onApply: (patch: CatalogBulkPatch) => void
    onClose: () => void
}) {
    const [field, setField] = useState<FieldKey>('classification')
    const [value, setValue] = useState('선행요건')
    const [confirm, setConfirm] = useState(false)

    const current = FIELDS.find((f) => f.key === field)!

    // 필드를 바꾸면 그 필드의 첫 선택지를 기본값으로 맞춘다.
    const onFieldChange = (next: FieldKey) => {
        setField(next)
        setValue(FIELDS.find((f) => f.key === next)!.options[0].value)
    }

    const valueLabel =
        current.options.find((o) => o.value === value)?.label ?? value

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
            onClick={onClose}>
            <Card
                role='dialog'
                aria-modal='true'
                aria-labelledby='bulk-edit-title'
                className='w-full max-w-sm p-5 shadow-sm'
                onClick={(e) => e.stopPropagation()}>
                <h2
                    id='bulk-edit-title'
                    className='text-foreground text-base font-semibold tracking-tight'>
                    일괄 편집
                </h2>
                <p className='text-muted-foreground mt-1 text-[13px]'>
                    선택한{' '}
                    <span className='text-foreground font-bold'>{count}</span>개
                    항목의 한 필드를 같은 값으로 바꿔요.
                </p>

                <div className='mt-4 space-y-3'>
                    <div className='space-y-1.5'>
                        <Label htmlFor='bulk-field'>필드</Label>
                        <Select
                            id='bulk-field'
                            value={field}
                            onChange={(e) =>
                                onFieldChange(e.target.value as FieldKey)
                            }>
                            {FIELDS.map((f) => (
                                <option key={f.key} value={f.key}>
                                    {f.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className='space-y-1.5'>
                        <Label htmlFor='bulk-value'>값</Label>
                        <Select
                            id='bulk-value'
                            value={value}
                            onChange={(e) => setValue(e.target.value)}>
                            {current.options.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className='mt-5 flex justify-end gap-2'>
                    <Button variant='outline' size='sm' onClick={onClose}>
                        취소
                    </Button>
                    <Button
                        size='sm'
                        disabled={busy}
                        onClick={() => setConfirm(true)}>
                        {busy ? '적용하는 중…' : '적용'}
                    </Button>
                </div>
            </Card>

            <ConfirmDialog
                open={confirm}
                danger
                title='일괄 편집을 적용할까요?'
                description={`선택한 ${count}개 항목의 [${current.label}] 값을 "${valueLabel}"(으)로 바꿔요. 평가 채점·모니터링에 영향을 줄 수 있어요.`}
                confirmLabel='적용'
                onCancel={() => setConfirm(false)}
                onConfirm={() => {
                    setConfirm(false)
                    onApply(toPatch(field, value))
                }}
            />
        </div>
    )
}
